import {
    Button,
    ButtonVariant,
    Container,
    ContainerHeader,
    Table,
} from '@Core/Components'
import { Loader } from '@Core/Components/Loader/Loader'
import { Accent, PageHeading } from '@/components/brand/brand'
import { usePurchaseProcesses } from '@Purchasing/Hooks/usePurchaseProcesses/usePurchaseProcesses'
import { RoutesPaths } from '@Purchasing/RoutesPaths'
import { PurchaseProcessStatus } from '@Purchasing/Domain/Model/PurchaseProcess'
import { formatRouteId } from '@Utils/Formatters/formatRouteId'
import { format } from 'date-fns'
import { ChangeEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FilterSelect } from '@/components/filter-select'
import { Button as UiButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { ChevronLeft, ChevronRight, KanbanSquare, List, Search } from 'lucide-react'
import { RightContent } from '@Entry/Common/Header/Header.styles'
import { StatusTag } from './PurchaseProcessList.styles'
import { PurchasingBoard } from './PurchasingBoard'
import { AssigneeFilter } from '@Purchasing/ui/AssigneeFilter'
import { AssigneeName } from '@Purchasing/ui/AssigneeName'

const ASSIGNEE_FILTER_KEY = 'purchasing-assignee-filter'

// Preferência de quem está vendo: lembra o último filtro de responsável.
const readAssigneeFilter = (): string[] => {
    try {
        const raw = window.localStorage.getItem(ASSIGNEE_FILTER_KEY)
        const parsed = raw ? JSON.parse(raw) : []
        return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : []
    } catch {
        return []
    }
}

const statusLabel: Record<PurchaseProcessStatus, string> = {
    DRAFT: 'Rascunho',
    ACTIVE: 'Ativo',
    LOST: 'Perdido',
    CANCELED: 'Cancelado',
    FINISHED: 'Finalizado',
}

export const PurchaseProcessList = () => {
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState<PurchaseProcessStatus | ''>('')
    const [page, setPage] = useState(1)
    const [assignees, setAssignees] = useState<string[]>(readAssigneeFilter)
    const assignedTo = assignees.join(',')
    const [view, setView] = useState<'list' | 'kanban'>(() =>
        window.localStorage.getItem('purchasing-process-view') === 'kanban' ? 'kanban' : 'list'
    )
    const { data, loading } = usePurchaseProcesses(
        page,
        10,
        search,
        status || undefined,
        assignedTo
    )
    const navigate = useNavigate()

    const handleNavigate = (id: string) => () => {
        navigate(formatRouteId(RoutesPaths.PurchasingDetail, id))
    }

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value)
        setPage(1)
    }

    const handleStatusChange = (value: string) => {
        setStatus(value as PurchaseProcessStatus | '')
        setPage(1)
    }

    const handleAssigneesChange = (next: string[]) => {
        setAssignees(next)
        setPage(1)
        try {
            window.localStorage.setItem(ASSIGNEE_FILTER_KEY, JSON.stringify(next))
        } catch {
            // armazenamento indisponível: o filtro vale só nesta sessão
        }
    }

    const changeView = (next: 'list' | 'kanban') => {
        setView(next)
        window.localStorage.setItem('purchasing-process-view', next)
    }

    return (
        <Container>
            <ContainerHeader>
                <PageHeading eyebrow="Compras">
                    Processos de <Accent>compra</Accent>
                </PageHeading>
                <RightContent>
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                            <Input
                                data-testid="purchasing-search"
                                aria-label="Buscar processos"
                                className="h-9 w-64 rounded-full pl-9"
                                placeholder="Buscar por código ou título"
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </div>
                        <FilterSelect
                            label="Filtrar status"
                            allLabel="Todos os status"
                            value={status}
                            onChange={handleStatusChange}
                            options={(Object.keys(statusLabel) as PurchaseProcessStatus[]).map((s) => ({ value: s, label: statusLabel[s] }))}
                        />
                        <AssigneeFilter value={assignees} onChange={handleAssigneesChange} />
                        <Button
                            onClick={() => navigate(RoutesPaths.PurchasingNew)}
                            variant={ButtonVariant.primary}
                        >
                            Novo Processo
                        </Button>
                    </div>
                </RightContent>
            </ContainerHeader>
            <div className="mb-4 inline-flex rounded-full bg-secondary p-1" role="group" aria-label="Visualização de processos">
                {([['list', 'Lista', List], ['kanban', 'Kanban', KanbanSquare]] as const).map(([value, label, Icon]) => (
                    <button
                        key={value}
                        type="button"
                        aria-pressed={view === value}
                        onClick={() => changeView(value)}
                        className={cn(
                            'inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
                            view === value
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:bg-accent hover:text-brand-deep'
                        )}
                    >
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                        {label}
                    </button>
                ))}
            </div>
            {view === 'kanban' ? <PurchasingBoard search={search} status={status || undefined} assignedTo={assignedTo} /> : loading ? <Loader /> : <Table>
                <thead>
                    <tr>
                        <th>Código</th>
                        <th>Título</th>
                        <th>Status</th>
                        <th>Estágio</th>
                        <th>Responsável</th>
                        <th>Criado em</th>
                    </tr>
                </thead>
                <tbody>
                    {data.items.length === 0 ? (
                        <tr>
                            <td colSpan={6}>
                                Nenhum processo de compra encontrado
                            </td>
                        </tr>
                    ) : (
                        data.items.map((process) => (
                            <tr
                                key={process._id}
                                onClick={handleNavigate(process._id)}
                            >
                                <td>{process.code}</td>
                                <td>{process.title}</td>
                                <td>
                                    <StatusTag status={process.status}>
                                        {statusLabel[process.status]}
                                    </StatusTag>
                                </td>
                                <td>{process.stageName ?? '-'}</td>
                                <td>
                                    <AssigneeName value={process.assignedTo} />
                                </td>
                                <td>
                                    {format(
                                        new Date(process.createdAt),
                                        'dd/MM/yyyy HH:mm'
                                    )}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>}
            {view === 'list' && <div className="mt-3 flex items-center gap-3">
                <UiButton type="button" variant="outline" size="sm" className="gap-1" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />Anterior
                </UiButton>
                <span className="text-sm text-muted-foreground">Página {page} de {Math.max(1, Math.ceil(data.total / data.limit))}</span>
                <UiButton type="button" variant="outline" size="sm" className="gap-1" disabled={page * data.limit >= data.total} onClick={() => setPage(page + 1)}>
                    Próxima<ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                </UiButton>
            </div>}
        </Container>
    )
}
